import { markerAlpha } from './common'
import { getIEVersion } from '~js/utils'
import { HOTSPOT_BASE_SIZE } from '@/api/constants'
import { actionTagXml } from './action-tag'
import { actionMemoXml } from './action-memo'
import { actionProcessGifXml } from './action-process-gif'
import { actionGalleryXml } from './action-gallery'

const getActionsXml = function (startIndex = 0, panoramas, defaultFov, krpanoXOffset, vrThumbAth, krpanoVrModeObj) {
  const firstPanoramaRotataionY = (panoramas[startIndex].panoramaRotation ? -panoramas[startIndex].panoramaRotation.y : 0) + krpanoXOffset
  const firstPanoramaRotataionX = panoramas[startIndex].panoramaRotation ? -panoramas[startIndex].panoramaRotation.x : 0

  // Basic actions. Called in krpanos actions only.
  const baseActions = `
    <action name="look_straight">
      tween(view.vlookat, 0, 1.0, easeInOutSine);
      tween(view.fov, ${defaultFov}, distance(150, 0.8));
    </action>

    <action name="normal_view">
      tween(view.architectural, 0.0, distance(2.0,1.5));
      tween(view.pannini, 0.0, distance(2.0,1.5));
      tween(view.fisheye, 0.0, distance(2.0,1.5));
    </action>

    <action name="planet_view">
      set(view.architectural, 0.0);
      set(view.pannini, 0.0);
      set(view.fisheye, 1.0);
    </action>

    <action name="correct_hlookat">
      mod(view.hlookat, 360);
      add(view.hlookat, 360);
      mod(view.hlookat, 360);
    </action>

    <action name="on_motion_end">
      <!-- %1 hook event name (optional)-->
      correct_hlookat();
      if(%1 != null, jscall(calc('krHooks.emit("%1")')));
    </action>

    <!-- 初始化 hotspot alpha -->
    <action name="init_hotspot_alpha" scope="local" args="name">
      set(hotspot[get(name)].alpha, 0);
    </action>

    <!-- 淡入 hotspot alpha -->
    <action name="fade_in_hotspot_alpha" scope="local" args="name, alpha">
      delayedcall(1, tween(hotspot[get(name)].alpha, get(alpha), 0.5, easeOutQuad););
    </action>
  `

  // Event Actions. Called in krpano hotspot events only.
  const eventActions = `
    <action name="on_point_click">
      <!-- %1 markerIndex -->
      jscall(calc('krHooks.emit("point_click", %1)'));
    </action>

    <action name="on_teleport_click">
      <!-- %1 markerIndex -->
      jscall(calc('krHooks.emit("teleport_click", %1)'));
    </action>

    <action name="fullscreen_videoplay">
      jscall(calc('krHooks.emit("fullscreen_videoplay", "%1")'));
    </action>

    <action name="on_video_start">
      jscall(calc('krHooks.emit("on_video_start", "%1")'));
    </action>

    <action name="on_video_play">
      jscall(calc('krHooks.emit("on_video_play", "%1")'));
    </action>

    <action name="on_video_paused">
      jscall(calc('krHooks.emit("on_video_paused", "%1")'));
    </action>

    <action name="on_video_complete">
      jscall(calc('krHooks.emit("on_video_complete", "%1")'));
    </action>

    <action name="hotspot_loaded">
      jscall(calc('krHooks.emit("hotspot_loaded", "%1")'));
      <!-- delay a little bit to prevent image flashing -->
      delayedcall(0.1, if(enabled, set(visible, true);););
    </action>

    <action name="remove_css3d_hotspots">
      for(set(i, 0), i LE hotspot.count, inc(i),
        calc(hs, hotspot[get(i)]);
        if(hs !== null AND hs.renderer == 'css3d', set(hs.keep, false));
      );
    </action>

    <!-- 影片載入並添加到DOM上 -->
    <action name="add_iframe" type="Javascript">
    <![CDATA[
      setTimeout(()=> {
        const iframe = document.createElement('iframe');
        iframe.allow = 'fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;';
        iframe.src = args[1];
        caller.registercontentsize(args[2], args[3]);
        caller.sprite.appendChild(iframe);
        caller.sprite.classList.add('video-embed');
      }, 1000)
    ]]>
    </action>

    <action name="add_custom_icon" type="Javascript">
    <![CDATA[
      const img = document.createElement('img');
      img.style.setProperty('opacity', args[4]);
      img.onload = function () {
        // 先註解掉，主要gif降解析度原因是registercontentsize又再次設定長寬後導致
        // const baseSize = ${HOTSPOT_BASE_SIZE};
        // const proportion = this.width / this.height;
        // const isPortrait = proportion < 1;
        // const width = isPortrait ? baseSize * proportion : baseSize;
        // const height = isPortrait ? baseSize : baseSize / proportion;
        // caller.width = width;
        // caller.height = height;
        caller.visible = true;
        // caller.registercontentsize(width, height);
      }
      img.src = args[1];
      // caller.registercontentsize(args[2], args[3]);
      caller.sprite.appendChild(img);
      caller.sprite.classList.add('custom-icon');
    ]]>
    </action>

    <action name="set_marker_scale_html" scope="parent">
      set(hotspot[%1].scale, %2);
      set(hotspot[%1].html, %3);
    </action>

    <action name="set_marker_position" scope="parent">
      set(hotspot[%1].ath, %2);
      set(hotspot[%1].atv, %3);
    </action>

    <action name="set_marker_size" scope="parent">
      set(hotspot[%1].width, %2);
      set(hotspot[%1].height, %3);
    </action>

    <action name="set_marker_offset" scope="parent">
      set(hotspot[%1].ox, %2);
      set(hotspot[%1].oy, %3);
    </action>

    <action name="replace_marker_url" scope="parent">
      <!-- %1 markerName -->
      <!-- %1 url -->
      set(hotspot[%1].url, %2);
    </action>
  `

  // 進入 LT 後，攝影機由面向下面轉為預設角度
  const hookActions = `
    <action name="startup">
      <!-- %1 to launch planet view -->
      <!-- ${process.env.NODE_ENV === 'dev' ? 'showlog();' : ''} -->
      if (%1,
        planet_view();
        tween(view.hlookat, ${firstPanoramaRotataionY}, 0.1);
        tween(view.vlookat, 90, 0.1);
        tween(view.fov, 150, 0.1);
        wait(1.5);
        tween(view.vlookat, ${firstPanoramaRotataionX}, 1.0, easeInOutSine);
        tween(view.fov, ${defaultFov}, distance(150, 0.8));
        normal_view();
        jscall(calc('krHooks.emit("startup")')); <!-- 必須在 wait1.5後 jscall 不能抽離 -->
      ,
        lookat(${firstPanoramaRotataionY}, 0, ${defaultFov});
        jscall(calc('krHooks.emit("startup")'));
      )
    </action>

    <action name="pan_to">
      <!-- %1 dest angle -->
      <!-- %2 speed -->
      <!-- %3 event hook name  -->
      pan(calc(%1 - view.hlookat + ${krpanoXOffset}), %2, %3);
    </action>

    <action name="pan">
      <!-- %1 angle -->
      <!-- %2 speed -->
      <!-- %3 event hook name  -->
      Math.abs(angleAbs, %1);
      trace(%3);
      set(duration, calc(angleAbs * %2));
      tween(view.hlookat, calc(view.hlookat + %1), get(duration), linear, on_motion_end(%3));
    </action>
  `
  const webXRCommonActions = `
    <action name="ini_webXR_common_variable">
      def(currentObjectId, string, %1);
    </action>

    <action name="close_webXR_marker" scope="local" args="opacity">
      if (get(currentObjectId) != null, marker_fadeout(get(currentObjectId), get(opacity)););

      set(hotspot[markerInfoBg].scale, 0);
      set(hotspot[markerCloseButton].scale, 0);
      replace_marker_url(markerCloseButton, ${require('../img/button/x-default.png')});
      set(hotspot[markerFavoriteButton].scale, 0);
      set(hotspot[markerInfoTitle].scale, 0);
      set(hotspot[markerInfoDescription].scale, 0);
      set(hotspot[markerInfoPrice].scale, 0);

      if (haveGallery ,
        for (set(i, 0), i LT imagesLength, inc(i),
          txtadd(markerName, 'markerInfoImage_', get(i), '_', get(currentObjectId));
          set(hotspot[%markerName].scale, 0);
        );
        txtadd(markerName, 'markerScaleUp_', get(currentObjectId));
        set(hotspot[%markerName].scale, 0);
        txtadd(markerName, 'markerScaleDown_', get(currentObjectId));
        set(hotspot[%markerName].scale, 0);
        txtadd(markerName, 'markerNext_', get(currentObjectId));
        set(hotspot[%markerName].scale, 0);
        txtadd(markerName, 'markerPrev_', get(currentObjectId));
        set(hotspot[%markerName].scale, 0);
      );
    </action>

    <action name="set_webXR_button_events">
      <!--
        %1 opacity,
        %2 price,
      -->
      <!-- close -->
      set(hotspot[markerCloseButton].onover,
        'reopen_webXR_marker(%2);
        replace_marker_url(markerCloseButton, ${require('../img/button/x-hover.png')});');

      set(hotspot[markerCloseButton].onout,
        'replace_marker_url(markerCloseButton, ${require('../img/button/x-default.png')});');

      set(hotspot[markerCloseButton].onclick,
        'replace_marker_url(markerCloseButton, ${require('../img/button/x-select.png')});
        delayedcall(.2, close_webXR_marker(%1););');

      <!-- favorite -->
      set(hotspot[markerFavoriteButton].onover,
        'reopen_webXR_marker(%2);
        replace_marker_url(markerFavoriteButton, ${require('../img/button/star-hover.png')});');

      set(hotspot[markerFavoriteButton].onout,
        'replace_marker_url(markerFavoriteButton, ${require('../img/button/star-default.png')});');

      set(hotspot[markerFavoriteButton].onclick,
        'replace_marker_url(markerFavoriteButton, ${require('../img/button/star-select.png')});');
    </action>

    <action name="reopen_webXR_marker" scope="local" args="price">
      set(hotspot['markerInfoBg'].scale, 1);
      set(hotspot['markerCloseButton'].scale, 1);
      set(hotspot['markerFavoriteButton'].scale, 1);
      set(hotspot['markerInfoTitle'].scale, 1);
      if (get(price) !== null,
        set(hotspot['markerInfoPrice'].scale, 1);
      );
      set(hotspot['markerInfoDescription'].scale, 1);

      <!-- with gallery -->
      if (haveMultiImages ,
        txtadd(markerName, 'markerNext_', get(currentObjectId));
        set(hotspot[%markerName].scale, 1);

        txtadd(markerName, 'markerPrev_', get(currentObjectId));
        set(hotspot[%markerName].scale, 1);
      );
      if (haveGallery ,
        for (set(i, 0), i LT imagesLength, inc(i),
          txtadd(markerName, 'markerInfoImage_', get(i), '_', get(currentObjectId));
          set(hotspot[%markerName].scale, 1);
        );
        txtadd(markerName, 'markerInfoImage_', get(currentGalleryIndex), '_', get(currentObjectId));
        set(hotspot[%markerName].scale, get(currentImageScale));

        txtadd(markerName, 'markerScaleUp_', get(currentObjectId));
        set(hotspot[%markerName].scale, 1);
        txtadd(markerName, 'markerScaleDown_', get(currentObjectId));
        set(hotspot[%markerName].scale, 1);
      );
    </action>
  `

  return `
  ${baseActions}
  ${eventActions}
  ${hookActions}
  ${webXRCommonActions}
  ${actionTagXml()}
  ${actionMemoXml()}
  ${actionProcessGifXml()}
  ${actionGalleryXml()}
  <action name="set_camera">
    <!-- %1 hlookat -->
    <!-- %2 vlookat -->
    <!-- %3 fov -->
    correct_hlookat();
    if(%1 != null,
      def(hOffset, number, 0);
      def(angle, number, calc(%1 - view.hlookat));
      if(angle GT 180, set(hOffset, -360););
      if(angle LT -180, set(hOffset, 360););
      <!-- 限制每秒最多轉45度 -->
      def(timeH, number, calc((%1 + hOffset - view.hlookat) / 45));
      Math.abs(timeH);
      if(timeH LT 1, set(timeH, 1););
      tween(view.hlookat, calc(%1 + hOffset), get(timeH), linear);
    );
    if(%2 != null,
      def(timeV, number, calc((%2 - view.vlookat) / 45));
      Math.abs(timeV);
      if(timeV LT 1, set(timeV, 1););
      tween(view.vlookat, %2, get(timeV), linear);
    );
    if(%3 != null, tween(view.fov, %3, 1, linear););
  </action>

  <action name="stop_camera">
    stoptween(view.hlookat, view.vlookat, view.fov);
  </action>

  <action name="do_crop_animation" scope="local" args="frameWidth, frameHeight, frameRate, frameLength, ROW, duration">
    <!-- define local variables -->
    def(local.xFrames, integer, get(ROW));
    set(local.frames, get(frameLength));
    def(local.frame, integer, 0);
    jsget(isVrMode, 'krHooks.isVrMode()');

    <!-- set the first frame -->
    calc(caller.crop, '0|0|' + frameWidth + '|' + frameHeight);

    <!-- do the animation -->
    setinterval(calc('crop_anim_' + caller.name), calc(duration / frameRate),
      if(caller.loaded AND isVrMode,
        inc(frame);
        if(frame GE frames, if(caller.onlastframe !== null, callwith(caller, onlastframe() ) ); set(frame,0); );
        mod(xPos, frame, xFrames);
        div(yPos, frame, xFrames);
        Math.floor(yPos);
        mul(xPos, frameWidth);
        mul(yPos, frameHeight);
        calc(caller.crop, xPos + '|' + yPos + '|' + frameWidth + '|' + frameHeight);
        ,
          <!-- stop the interval when the hotspot gets removed and exit vr mode-->
          clearinterval(calc('crop_anim_' + caller.name));
      );
    );
  </action>

  <action name="prepare_change_scene">
  <!-- 儲存當前仰角 All javascript call this action to trigger change scene here first
    %1 = next scene name
    %2 = next scene panoramaId
    %3 = selectedMethod
    %4 = next scene hlookat offset (for marker)
    %5 = is clicked from marker point
    %6 = marker ath
    %7 = marker objectId
  -->
  <!-- 把 vr 裡的 marker 對應 info 顯示/隱藏 -->
    set(style[vr_panorama_style].scale, 0);
    ${(() => {
      let result = ''
      const calc = '+'
      for (let i = 0; i < panoramas.length; i++) {
        const horizontalCount = Math.floor(i / 4)
        result += `hotspot[vr_panorama_${i}].loadstyle(vr_panorama_style);
          hotspot[vr_panorama_text_${i}].loadstyle(vr_panorama_style);
          set(hotspot[vr_panorama_${i}].ath, calc(view.hlookat ${calc} ${vrThumbAth * horizontalCount}));
          set(hotspot[vr_panorama_text_${i}].ath, calc(view.hlookat ${calc} ${vrThumbAth * horizontalCount}));`
      }
      return result
    })()}
    jscall(calc('krHooks.prepareChangeScene("%1", "%2", "%3", "%4", %5, %6, "%7")'));
  </action>

  <action name="change_scene">
    <!--
      change scene in krpano, and callback to javascript (auto call it from prepare_change_scene)
      %1 ~ %6 is all same with prepare_change_scene without marker objectId
      %7 = newIndex
      %8 = oldIndex
      %9 = oldHLookat
      %10 is gyro enabled
    -->
    jscall(calc('krHooks.stopAutoRotate()'));
    stoptween(view.hlookat, view.vlookat, view.fov); <!-- 暫時停止導覽的視角操作 -->
    if (%10 == true, set(plugin[gyro].enabled, false);); <!-- 切換場景時 Gyro 需要先關閉，切換後設定好 h 視角，再打開 -->
    if (webvr.isenabled AND webvr.headtracking == true, set(webvr.headtracking, false););

    jscall(calc('krHooks.changeImage("%2", "%3", %5, ' + webvr.isenabled + ')'));
    def(prevVlookat, number, calc(view.vlookat)); <!-- 儲存當前仰角 -->
    def(prevHlookat, number, calc(view.hlookat)); <!-- 儲存當前視角 -->

    if (%5,
      ${(() => {
        if (getIEVersion()) {
          return 'loadscene(%1, null, MERGE, BLEND(1));'
        } else {
          // non-IE do zoom moving
          // ref: https://krpano.com/examples/simple3dtransition/example3.html
          return `spheretospace(caller.ath, caller.atv, caller.depth, tx, ty, tz);
            loadscene(%1, null, MERGE, OPENBLEND(1, 0.0, 0.75, 0.05, linear));
            tween(caller.alpha, 0.0, 0.25, default);`
        }
      })()},
      loadscene(%1, null, MERGE, BLEND(1));
    );

    <!-- 把 vr 裡的 marker 對應 info 顯示/隱藏 -->
    if(webvr.isenabled, ${(() => {
      let result = ''
      krpanoVrModeObj.vrModeShouldShow.forEach((item) => {
        if (!item.startsWith('markerInfoImage_')) {
          result += ` set(hotspot[${item}].visible,true);`
        }
      })
      krpanoVrModeObj.vrModeShouldHide.forEach((item) => {
        result += ` set(hotspot[${item}].visible,false);`
      })
      return result
    })()});

    def(defaultViewHlookat, number, calc(view.hlookat));
    <!-- 非 Point 時，使用前一個 camera 仰角 -->
    if (%5 != true, set(view.vlookat, calc(prevVlookat)););
    def(hlookatOffset, number, calc(%6 - prevHlookat));
    <!-- Point 已指定視角，不需要再計算偏移 -->
    if (%5 == true, set(hlookatOffset, 0));
    if (%4, set(view.hlookat, calc(%4 - hlookatOffset)));
    if (%10, set(plugin[gyro].enabled, true);); <!-- 若有啟動 Gyro，在這裡要重新打開 -->
    if (webvr.isenabled AND webvr.headtracking == false, set(webvr.headtracking, true););

    <!-- 判斷下一個場景是否是 isTopLogo，要切換顯示 -->
    if (scene[get(xml.scene)].name == '%1' AND scene[get(xml.scene)].isTopLogo == 'true',
    set(hotspot[topLogoTripod].visible, true);,
    set(hotspot[topLogoTripod].visible, false););

    <!-- panorama 有指南針資訊，決定指南針要不要出現 -->
    if (scene[get(xml.scene)].name == '%1' AND scene[get(xml.scene)].compassDegree != undefined,
    set(hotspot[compassTripod].visible, true); set(hotspot[compassTripod].rotate, calc(${krpanoXOffset} - scene[get(xml.scene)].compassDegree));,
    set(hotspot[compassTripod].visible, false););

    ${(() => {
      if (!getIEVersion()) {
        return 'wait(LOAD);'
      } else {
        return `wait(LOAD);
    wait(BLEND);`
      }
    })()}

    <!-- 依照 default view 決定自動旋轉的方向 -->
    def(autoRotateRevert, boolean, false);
    if (%5,
      set(view.oz, 500);
      tween(view.oz, 0);
      def(rotateAngleRight, number, calc(defaultViewHlookat - view.hlookat));
      mod(rotateAngleRight, 360);
      if (rotateAngleRight LT 0, set(rotateAngleRight, calc(rotateAngleRight + 360)););
      def(rotateAngleLeft, number, calc(360 - rotateAngleRight));
      if (rotateAngleRight LT rotateAngleLeft, set(autoRotateRevert, true););
    );
    jscall(calc('krHooks.emit("change_scene", ' + autoRotateRevert + ')'));
    jsget(isVrMode, 'krHooks.isVrMode()');
    if (isVrMode,
      processed_gif();
      processed_image();
    );
  </action>

  <action name="toggle_vr_menu">
    <!-- toggle show/hide menu in vr mode -->
    if (style[vr_panorama_style].scale == 1,
      set(style[vr_panorama_style].scale, 0);
    ,
      set(style[vr_panorama_style].scale, 1);
    );
    ${(() => {
      let result = ''
      const calc = '+'
      for (let i = 0; i < panoramas.length; i++) {
        const horizontalCount = Math.floor(i / 4)
        result += `hotspot[vr_panorama_${i}].loadstyle(vr_panorama_style);
          hotspot[vr_panorama_text_${i}].loadstyle(vr_panorama_style);
          set(hotspot[vr_panorama_${i}].ath, calc(view.hlookat ${calc} ${vrThumbAth * horizontalCount}));
          set(hotspot[vr_panorama_text_${i}].ath, calc(view.hlookat ${calc} ${vrThumbAth * horizontalCount}));`
      }
      return result
    })()}
  </action>

  <action name="nextscene_loop">
    <!-- %1 Next Panorama: 1, Prev Panorama: -1 -->
    if(xml.scene != null AND scene.count GT 1,
      if (scene[get(xml.scene)].index == 0,
        add(newsceneindex, scene[get(xml.scene)].index, calc(%1 + 1));,
        add(newsceneindex, scene[get(xml.scene)].index, %1););
      sub(lastsceneindex, scene.count, 1);
      if(newsceneindex LT 1, copy(newsceneindex, lastsceneindex));
      if(newsceneindex GT lastsceneindex, set(newsceneindex, 1));
      def(selectedMethod, string, 'VrModePrev');
      if(%1 == 1, set(selectedMethod, 'VrModeNext'));
      prepare_change_scene(get(scene[get(newsceneindex)].name), get(scene[get(newsceneindex)].objectId), get(selectedMethod), 0);
    );
  </action>

  <action name="marker_mousein">
  <!-- marker hover -->
    marker_fadein(%1);
    if(webvr.isenabled,
      if (%3 == point,
        set(hotspot[markerInfoHover].scale, 1);
        set(hotspot[markerInfoHover].ath, %4);
        set(hotspot[markerInfoHover].atv, %5);
        set(hotspot[markerInfoHover].html, %7);
      );
    ,
      spheretoscreen(hotspot[markerInfo_%1].ath, hotspot[markerInfo_%1].atv, mouseX, mouseY);
      jscall(calc('krHooks.markerMousein(%2, ' + mouseX + ', ' + mouseY + ')'));
    );
  </action>

  <action name="set_marker_info">
    jscall(calc('krHooks.setMarkerInfo(%1)'));
  </action>

  <action name="set_marker_narration">
    jscall(calc('krHooks.setMarkerNarration(%1)'));
  </action>

  <action name="set_search_setting">
    jscall(calc('krHooks.setSearchSetting(%1)'));
  </action>

  <action name="marker_mouseout">
    <!-- marker mouse leave -->
    if (%1 != null, marker_fadeout(%1, %4););
    if(webvr.isenabled,
      if (%3 == point, set(hotspot[markerInfoHover].scale, 0););
    ,
      jscall(calc('krHooks.markerMouseout(%2)'));
    );
  </action>

  <action name="marker_fadein">
    <!-- marker alpha to 1 -->
    tween(hotspot[marker_%1].alpha, 1, .3, easeOutQuad);
  </action>

  <action name="marker_fadeout" scope="parent">
    <!-- marker alpha to ${markerAlpha} -->
    tween(hotspot[marker_%1].alpha, %2, .3, easeOutQuad);
  </action>

  <action name="change_camera">
    <!-- set camera to store-->
    correct_hlookat();
    jscall(calc('krHooks.changeCamera(' + view.hlookat + ', ' + view.vlookat + ', ' + view.fov + ')'));
  </action>

  <action name="handle_show_popup">
    <!-- click popup marker show popup -->
    jscall(calc('krHooks.handleShowPopup(%1)'));
  </action>

  <action name="webvr_onentervr">
    processed_gif();
    processed_image();
    ${(() => {
      let result = ''
      krpanoVrModeObj.vrModeShouldShow.forEach((item) => {
        if (!item.startsWith('markerInfoPhoto_')) {
          result += ` set(hotspot[${item}].visible,true);`
        }
      })
      krpanoVrModeObj.vrModeShouldHide.forEach((item) => {
        result += `set(hotspot[${item}].visible,false);`
      })
      return result
    })()}
    jscall(calc('krHooks.stopAutoRotate()'));
    webvr_showbuttons();
    webvr_hide_all_non_vr_layers();
    if(webvr.isfake, webvr_show_fakemode_info(true););
  </action>

  <action name="webvr_onexitvr">
    revert_hs_properties();
    ${(() => {
      let result = ''
      krpanoVrModeObj.vrModeShouldShow.forEach((item) => {
        result += `set(hotspot[${item}].visible,false);`
      })
      krpanoVrModeObj.vrModeShouldHide.forEach((item) => {
        result += `set(hotspot[${item}].visible,true);`
      })
      return result
    })()}
    stopdelayedcall(vr_button_fadeout);
    tween(layer[webvr_exitbutton].alpha,0);
    tween(layer[webvr_setupbutton].alpha,0);
    webvr_show_fakemode_info(false);
    webvr_restore_layers();
    jscall(calc('krHooks.startAutoRotate()'));
    jscall(calc('krHooks.exitVrMode()'));
  </action>

  <action name="vr_menu_following" type="Javascript" devices="html5"><![CDATA[
    var hs1 = krpano.get("hotspot[vr_menu_bg]");
    var hs2 = krpano.get("hotspot[vr_menu_l]");
    var hs3 = krpano.get("hotspot[vr_menu]");
    var hs4 = krpano.get("hotspot[vr_menu_r]");
    if (!hs1.hovering && !hs2.hovering && !hs3.hovering && !hs4.hovering) {
      var dh;
      var f = 0.01;
      var h = krpano.view.hlookat;
      var v = krpano.view.vlookat;
      var hsh = hs1.ath;
      var hsv = hs1.atv;
      h = (h-(h|0)) + (((h|0)+360180)%360) - 180.0;
      hsh = (hsh-(hsh|0)) + (((hsh|0)+360180)%360) - 180.0;
      dh = h - hsh;
      dh += (dh>180) ? -360 : (dh<-180) ? 360 : 0
      hsh += dh*f;
      var a = Math.abs(v - hs1.atv) / 90.0;
      a = 0.5 * Math.max(1.0 - 2.0 * Math.sqrt(a), 0);
      v = v + 55.0 - v*1.5;
      hsv = hsv*(1-f) + v*f;
      hs1.alpha = a;
      if(!hs2.hovering) hs2.alpha = a;
      if(!hs3.hovering) hs3.alpha = a;
      if(!hs4.hovering) hs4.alpha = a;
      hs4.ath = hs3.ath = hs2.ath = hs1.ath = hsh;
      hs4.atv = hs3.atv = hs2.atv = hs1.atv = hsv;
    }
  ]]></action>

  <action name="vr_menu_over">
    callwith(hotspot[vr_menu_bg], tween(alpha|depth,1.0|650); );
    callwith(hotspot[vr_menu_l], tween(alpha|depth,0.5|650); );
    callwith(hotspot[vr_menu], tween(alpha|depth,0.5|650); );
    callwith(hotspot[vr_menu_r], tween(alpha|depth,0.5|650); );
  </action>

  <action name="vr_menu_out">
    if(hotspot[vr_menu].hovering == false,
      callwith(hotspot[vr_menu_bg], tween(alpha|depth,0.25|800); );
      callwith(hotspot[vr_menu_l], tween(alpha|depth,0.25|800); );
      callwith(hotspot[vr_menu], tween(alpha|depth,0.25|800); );
      callwith(hotspot[vr_menu_r], tween(alpha|depth,0.25|800); );
    );
  </action>

  <action name="zoom_in">
    tween(view.fov, calc(view.fov - 30), .3, easeOutQuad);
  </action>

  <action name="zoom_out">
    tween(view.fov, calc(view.fov + 30), .3, easeOutQuad);
  </action>

  <layer name="webvr_exitbutton" keep="true" vr="true"
    style="webvr_button_style"
    html="Exit VR"
    align="top" y="24"
    autoalpha="true" alpha="0.0"
    onclick="jscall(calc('krHooks.exitVrMode()'));"
  />

  <action name="start_gyro">
    set('plugin[gyro].enabled', 'true');
  </action>

  <action name="stop_gyro">
    set('plugin[gyro].enabled', 'false');
  </action>

  <action name="hover_closest_point_marker">
    <!-- 得到公用參數 m_ath, m_atv -->
    screentosphere(mouse.x, mouse.y, m_ath, m_atv);
    jscall(calc('krHooks.findClosestPointMarker(' + m_ath + ', ' + m_atv + ', krHooks.hoverClosestPointMarker);'));
  </action>

  <action name="click_krpano_screen">
    jscall(calc('krHooks.clickKrpanoScreen();'));
  </action>

  <action name="enter_closest_point_marker">
    <!-- 滑鼠點的位置 -->
    screentosphere(mouse.x, mouse.y, m_ath, m_atv);
    jscall(calc('krHooks.findClosestPointMarker(' + m_ath + ', ' + m_atv + ', krHooks.enterClosestPointMarker);'));
  </action>

  <action name="change_building">
    <!--
      %1 = nextBuildingId
      %2 = nextBuildingIndex
      %3 = objectId
    -->
    jscall(calc('krHooks.changeBuilding("%1", %2, "%3");'));
  </action>

  <action name="get_value">
    jscall(calc('krHooks.resolve("get_value", ' + %1 + ')'))
  </action>

  <action name="set_value">
    set(%1,%2);
  </action>
`
}

export default getActionsXml
